/* eslint-disable jsx-a11y/no-autofocus --
 * The single autoFocus in this file moves focus into a control the user just
 * summoned (an inline editor that appears on their own click, or a modal's
 * first field). That is required focus management: leaving focus behind
 * strands keyboard users on an element that has since vanished. The rule
 * guards against autofocus on PAGE LOAD, which is a different thing and is
 * not done anywhere here. File-scoped rather than line-scoped because
 * Prettier reformats JSX props across lines, which silently detaches a
 * line-targeted disable from what it was suppressing.
 */
import { Button, Form, Input } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { CustomElementType } from '@/api/customElements';
import { BmoModal } from '@/components/BmoModal';

type Props = {
  open: boolean;
  kind: CustomElementType;
  saving: boolean;
  onCancel: () => void;
  /** Caller builds + strips the actual payload and POSTs it — this modal only collects the name. */
  onSubmit: (values: { nameEn: string; nameFr: string | null }) => Promise<void>;
};

const TITLE_KEY: Record<CustomElementType, string> = {
  Field: 'workflow.customElements.saveModalTitleField',
  Section: 'workflow.customElements.saveModalTitleSection',
  Step: 'workflow.customElements.saveModalTitleStep',
};

/**
 * Single reusable "name it" modal shared by the field/section/step save-as-custom
 * buttons. Parameterized by `kind` only for copy (title) — the stripped payload is
 * built and posted by the caller, since each kind's shape differs.
 */
export function SaveAsCustomElementModal({ open, kind, saving, onCancel, onSubmit }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ nameEn: string; nameFr?: string }>();

  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    await onSubmit({ nameEn: values.nameEn, nameFr: values.nameFr?.trim() ? values.nameFr : null });
  };

  return (
    <BmoModal
      open={open}
      onCancel={onCancel}
      title={t(TITLE_KEY[kind])}
      width={480}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={() => void handleOk()}>
          {t('common.save')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="nameEn"
          label={t('workflow.customElements.nameEn')}
          rules={[{ required: true, message: t('workflow.customElements.nameEnRequired') }]}
        >
          <Input maxLength={120} autoFocus />
        </Form.Item>
        <Form.Item name="nameFr" label={t('workflow.customElements.nameFr')}>
          <Input maxLength={120} />
        </Form.Item>
      </Form>
    </BmoModal>
  );
}
